/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Motion + structure for every landing page except the home page (which has its own,
 * see home.ts). It gives the inner pages the home page's grammar:
 *
 *  - chapters   every section gets an accent (cycling signal / ok / warn / violet), a
 *               "// 02 / 07" index, a word-mask headline with an accent tail, and
 *               an opaque "sheet" background
 *  - intro      hero headline words rise, panel unrolls
 *  - reveal     cards and tables rise in as their chapter arrives
 *  - sheets     the section underneath slows and dims while the next one slides over it
 *
 * `prepareChapters()` is idempotent and also used by the chapter rail.
 * `initPage()` returns a cleanup that reverts every tween/trigger.
 */
import { prefersReducedMotion, registerGsap } from "./gsap";
import { getLenis } from "./smooth-scroll";

export type Accent = "signal" | "ok" | "warn" | "violet";
export type Chapter = { el: HTMLElement; label: string; accent: Accent };

const CYCLE: Accent[] = ["signal", "ok", "warn", "violet"];
const pad = (n: number) => String(n).padStart(2, "0");

/** Sections of the current page, in order (hero first). Empty on the home page. */
function pageSections(): HTMLElement[] {
  const main = document.querySelector<HTMLElement>("main");
  if (!main || main.classList.contains("home-main")) return [];
  return Array.from(main.children).filter((n): n is HTMLElement => n instanceof HTMLElement && n.tagName === "SECTION");
}

const clean = (s: string | null | undefined) => (s || "").replace(/\s+/g, " ").trim();

export function prepareChapters(): Chapter[] {
  const sections = pageSections();
  if (!sections.length) return [];

  const heroAccent = (sections[0].dataset.accent as Accent) || "signal";
  const start = Math.max(0, CYCLE.indexOf(heroAccent));
  const hasTitle = (s: HTMLElement) => !!s.querySelector(":scope > .container > .section-title");

  let n = 1;
  return sections.map((el, i) => {
    const isHero = i === 0 && el.classList.contains("page-hero");
    const isCta = !!el.querySelector(".cta-box");
    let accent: Accent;
    if (isHero) accent = heroAccent;
    else accent = (el.dataset.accent as Accent) || CYCLE[(start + i) % 4];
    el.dataset.accent = accent;

    if (!isHero) {
      el.classList.add("page-chapter");
      if (isCta) el.classList.add("page-cta");
      /* The second section is the only covering sheet. The footer covers the close. */
      if (i === 1) el.classList.add("is-cover");
    } else {
      el.classList.add("page-sheet");
    }

    let label = "";
    if (isHero) {
      label = clean(el.querySelector(".home-idx")?.textContent).replace(/^\d+\s*/, "") || "Intro";
    } else if (isCta) {
      label = "Start";
    } else if (hasTitle(el)) {
      n += 1;
      label = clean(el.querySelector(".section-title")?.textContent);
    } else {
      label = clean(el.querySelector("h2, h3, h4")?.textContent) || "Details";
    }

    if (!isHero && !el.querySelector(":scope > .container > .home-idx, :scope .cta-content > .home-idx")) {
      const idx = document.createElement("p");
      idx.className = "home-idx is-injected";
      idx.textContent = isCta ? "Start" : pad(n);
      const title = el.querySelector(":scope > .container > .section-title");
      const ctaContent = el.querySelector(".cta-content");
      if (title) title.parentElement!.insertBefore(idx, title);
      else if (ctaContent) ctaContent.insertBefore(idx, ctaContent.firstChild);
    }
    return { el, label: label.length > 26 ? label.slice(0, 25).trimEnd() + "…" : label, accent };
  });
}

/* ------------------------------------------------------------------ text */

type Words = HTMLElement & { __words?: HTMLElement[]; __tail?: boolean };

/** Wrap the last one or two words in <em> (accent colour), like the home headlines. */
function accentTail(el: Words) {
  if (el.__tail || el.querySelector("em")) {
    el.__tail = true;
    return;
  }
  el.__tail = true;
  /* Server-rendered headings can be several adjacent text nodes ("What is ", "SMTP-X", "?"): merge them. */
  if (!el.children.length) el.textContent = clean(el.textContent);
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let last: Text | null = null;
  while (walker.nextNode()) {
    const t = walker.currentNode as Text;
    if (t.nodeValue && t.nodeValue.trim()) last = t;
  }
  if (!last) return;
  const text = last.nodeValue || "";
  const words = text.trimEnd().split(/\s+/);
  const total = clean(el.textContent).split(" ").length;
  const take = Math.min(total >= 5 ? 2 : 1, words.length);
  if (take < 1) return;
  /* Start offset of the take-th last word (a plain lastIndexOf would match "in" inside "minutes"). */
  const starts = Array.from(text.trimEnd().matchAll(/\S+/g), (m) => m.index ?? 0);
  const idx = starts[starts.length - take];
  const head = text.slice(0, idx);
  const tailText = text.slice(idx);
  const em = document.createElement("em");
  em.textContent = tailText;
  last.nodeValue = head;
  last.parentNode!.insertBefore(em, last.nextSibling);
}

function splitWords(el: Words | null): HTMLElement[] {
  if (!el) return [];
  if (el.__words) return el.__words;
  accentTail(el);
  el.setAttribute("aria-label", clean(el.textContent));
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes: Node[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const frag = document.createDocumentFragment();
    (node.nodeValue || "").split(/(\s+)/).forEach((part) => {
      if (!part) return;
      if (/^\s+$/.test(part)) {
        frag.appendChild(document.createTextNode(" "));
        return;
      }
      const w = document.createElement("span");
      w.className = "w";
      w.setAttribute("aria-hidden", "true");
      const wi = document.createElement("span");
      wi.className = "wi";
      wi.textContent = part;
      w.appendChild(wi);
      frag.appendChild(w);
    });
    node.parentNode!.replaceChild(frag, node);
  });
  el.__words = Array.from(el.querySelectorAll<HTMLElement>(".wi"));
  return el.__words;
}

/* ------------------------------------------------------------------ main */

const REVEAL = [
  ".value-card",
  ".faq-item",
  ".compare-card",
  ".process-step",
  ".quote-card",
  ".price-card",
  ".contact-card",
  ".feature",
  ".stat",
  ".tool",
  ".compare",
  ".article-toc",
  ".cta-actions",
].join(",");

export function initPage(): () => void {
  const chapters = prepareChapters();
  if (!chapters.length) return () => {};

  const reduced = prefersReducedMotion();
  /* Headlines are split even for reduced motion so the accent tail still shows. */
  chapters.forEach(({ el }) => {
    const h = el.querySelector<Words>("h1, :scope > .container > .section-title, .cta-content h2");
    if (h) accentTail(h);
  });
  if (reduced) {
    chapters.forEach(({ el }) => el.classList.add("is-in"));
    return () => {};
  }

  const { gsap, ScrollTrigger } = registerGsap();
  const lenis = getLenis();
  const ease = "expo.out";
  const mm = gsap.matchMedia();

  const ctx = gsap.context(() => {
    /* ---------------------------------------------------------- hero intro */
    const hero = chapters[0].el;
    if (hero.classList.contains("page-hero")) {
      const h1 = hero.querySelector<Words>("h1");
      const words = splitWords(h1);
      const idx = hero.querySelector(".home-idx");
      const lead = hero.querySelector(".lead");
      const actions = hero.querySelector(".hero-actions");
      const object = hero.querySelector(".home-object");
      const rows = hero.querySelectorAll(".home-sheet-row, .hero-term-line");

      gsap.set(words, { yPercent: 118 });
      gsap.set([idx, lead, actions].filter(Boolean), { autoAlpha: 0, y: 22 });
      if (object) gsap.set(object, { autoAlpha: 0, y: 48, clipPath: "inset(0% 0% 100% 0%)" });
      gsap.set(rows, { autoAlpha: 0, y: 14 });

      const tl = gsap.timeline({ defaults: { ease }, delay: 0.1 });
      tl.to(idx, { autoAlpha: 1, y: 0, duration: 1 }, 0);
      tl.to(words, { yPercent: 0, duration: 1.4, stagger: 0.07 }, 0.1);
      tl.to(lead, { autoAlpha: 1, y: 0, duration: 1.1 }, 0.55);
      tl.to(actions, { autoAlpha: 1, y: 0, duration: 1.1 }, 0.7);
      if (object) tl.to(object, { autoAlpha: 1, y: 0, clipPath: "inset(0% 0% 0% 0%)", duration: 1.5, clearProps: "clipPath" }, 0.3);
      tl.to(rows, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.09 }, 0.9);
    }

    /* ------------------------------------------------------------ chapters */
    let notePass = 0;
    chapters.forEach(({ el }, i) => {
      if (i === 0) return;
      const title = el.querySelector<Words>(":scope > .container > .section-title, .cta-content h2");
      const idx = el.querySelector(".home-idx");
      const sub = el.querySelector(":scope > .container > .section-sub, .cta-content > p:not(.home-idx)");
      const words = splitWords(title);

      gsap.set(words, { yPercent: 118 });
      gsap.set([idx, sub].filter(Boolean), { autoAlpha: 0, y: 18 });

      ScrollTrigger.create({
        trigger: el,
        start: "top 78%",
        once: true,
        onEnter: () => {
          el.classList.add("is-in");
          const tl = gsap.timeline({ defaults: { ease } });
          if (idx) tl.to(idx, { autoAlpha: 1, y: 0, duration: 1 }, 0);
          tl.to(words, { yPercent: 0, duration: 1.3, stagger: 0.06 }, 0.05);
          if (sub) tl.to(sub, { autoAlpha: 1, y: 0, duration: 1.1 }, 0.35);
        },
      });

      const items = Array.from(el.querySelectorAll<HTMLElement>(REVEAL)).filter((n) => !n.closest(".page-hero"));
      if (i > 1) revealChapter(el, items);
      else if (items.length) {
        gsap.set(items, { autoAlpha: 0, y: 34 });
        ScrollTrigger.batch(items, {
          start: "top 92%",
          once: true,
          onEnter: (batch: Element[]) =>
            gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1.1, ease, stagger: 0.07, overwrite: true, clearProps: "transform,opacity,visibility" }),
        });
      }
    });

    /* One covering sheet, then the footer. Content between them just reveals. */
    function lagUnder(el: HTMLElement, next: HTMLElement) {
      el.setAttribute("data-lag", "");
      gsap.fromTo(
        el,
        { "--lag": "0px", "--dim": 0 } as any,
        {
          "--lag": () => `${Math.round(window.innerHeight * 0.3)}px`,
          "--dim": 0.6,
          ease: "none",
          immediateRender: true,
          scrollTrigger: {
            trigger: next,
            start: "top bottom",
            end: "top top",
            scrub: lenis ? 0.4 : 0.8,
            invalidateOnRefresh: true,
          },
        } as any
      );
    }

    function revealChapter(el: HTMLElement, items: HTMLElement[]) {
      const rise = (targets: Element[], vars: Record<string, unknown>, stagger = 0.08) => {
        if (!targets.length) return;
        ScrollTrigger.create({
          trigger: el,
          start: "top 86%",
          once: true,
          onEnter: () => gsap.to(targets, { ...vars, duration: 1.05, ease, stagger, overwrite: true, clearProps: "transform,opacity,visibility,filter" }),
        });
      };
      const cards = Array.from(el.querySelectorAll<HTMLElement>(":scope .compare-card"));
      const rows = Array.from(el.querySelectorAll<HTMLElement>(":scope .compare .row"));
      const notes = Array.from(el.querySelectorAll<HTMLElement>(":scope .faq-item"));
      const actions = el.querySelector(".cta-actions");
      if (el.classList.contains("page-cta") && actions) {
        gsap.set(actions, { autoAlpha: 0, y: 20 });
        rise([actions], { autoAlpha: 1, y: 0 }, 0);
        return;
      }
      if (rows.length > 1 && !cards.length) {
        gsap.set(rows, { autoAlpha: 0, x: -28 });
        rise(rows, { autoAlpha: 1, x: 0 }, 0.09);
        return;
      }
      if (cards.length >= 2) {
        cards.forEach((n, k) => gsap.set(n, { autoAlpha: 0, x: k % 2 ? 72 : -72 }));
        rise(cards, { autoAlpha: 1, x: 0 }, 0.12);
        return;
      }
      if (notes.length) {
        const pass = notePass;
        notePass += 1;
        if (pass % 2 === 0) {
          gsap.set(notes, { autoAlpha: 0, y: 24, filter: "blur(10px)" });
          rise(notes, { autoAlpha: 1, y: 0, filter: "blur(0px)" }, 0.14);
        } else {
          notes.forEach((n, k) => gsap.set(n, { autoAlpha: 0, y: k % 2 ? 18 : 56 }));
          rise(notes, { autoAlpha: 1, y: 0 }, 0.1);
        }
        return;
      }
      if (items.length) {
        items.forEach((n, k) => gsap.set(n, { autoAlpha: 0, y: k % 2 ? 18 : 56 }));
        rise(items, { autoAlpha: 1, y: 0 }, 0.1);
      }
    }

    /* -------------------------------------------------------------- sheets */
    mm.add("(min-width: 901px)", () => {
      const footer = document.querySelector<HTMLElement>(".site-footer");
      const hero = chapters[0]?.el;
      const cover = chapters[1]?.el;
      const last = chapters[chapters.length - 1]?.el;
      if (hero && cover) lagUnder(hero, cover);
      if (last && footer && last !== cover) lagUnder(last, footer);
    });
  });

  return () => {
    mm.revert();
    ctx.revert();
    chapters.forEach(({ el }) => {
      el.removeAttribute("data-lag");
      el.style.removeProperty("--lag");
      el.style.removeProperty("--dim");
    });
  };
}
